import { useEffect, useRef, useState } from 'react';

/**
 * Calls function with a given interval
 * @param fn - function that will be called after the interval
 * @param interval - number of milliseconds the interval should wait before the specified function is executed
 * @returns - object with start, stop, toggle and active properties
 * Return object:

    start – start interval
    stop – stop interval
    toggle – toggle interval
    active – current interval status
  * @classdesc
  * useInterval is a hook that calls a function with a given interval. It returns an object with start, stop, toggle and active properties.
    ```tsx
    function useInterval(
        fn: () => void,
        interval: number
        ): {
            start: () => void;
            stop: () => void;
            toggle: () => void;
            active: boolean;
        };
    ```
 */

export function useInterval(fn: () => void, interval: number) {
    const [active, setActive] = useState(false);
    const intervalRef = useRef<number | undefined>(undefined);
    const fnRef = useRef<(() => void) | undefined>(undefined);

    useEffect(() => {
        fnRef.current = fn;
    }, [fn]);

    const start = () => {
        setActive((old) => {
            if (!old && !intervalRef.current) {
                intervalRef.current = window.setInterval(fnRef.current!, interval);
            }
            return true;
        });
    };

    const stop = () => {
        setActive(false);
        window.clearInterval(intervalRef.current);
        intervalRef.current = undefined;
    };

    const toggle = () => {
        if (active) {
            stop();
        } else {
            start();
        }
    };

    return { start, stop, toggle, active };
}
